'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { reservationColumn } from '@/components/reservation/reservation-col';
import Input from '@/components/ui/form-fields/input';
import Pagination from '@/components/ui/pagination';
import Text from '@/components/ui/typography/text';
import Table from '@/components/ui/table';

export default function TransactionActivity() {
  const [order, setOrder] = useState<string>('desc');
  const [column, setColumn] = useState<string>('');
  const [data, setData] = useState<any[]>([]); // Initialize table data
  const [searchfilter, setSearchFilter] = useState('');
  const [current, setCurrent] = useState(1);
  const [Rentals, setRentals] = useState<any[]>([]); // Store fetched data
  const [userid, setUserId] = useState<string | null>(null); // ✅ Store session user ID safely
  const burl = `${process.env.NEXT_PUBLIC_API}`;

  // ✅ Use useEffect to safely retrieve sessionStorage
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        const storedUserId = sessionStorage.getItem('userId');
        setUserId(storedUserId);
      }
    } catch (error) {
      console.error('sessionStorage is not available:', error);
    }
  }, []);

  // ✅ Fetch rentals only after userId is available
  useEffect(() => {
    if (!userid) return;

    async function fetchRentals() {
      try {
        const response = await fetch(`${burl}/getlistallbookings.php`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userid }), // ✅ Now dynamically fetched
        });

        if (!response.ok) {
          throw new Error('Failed to fetch rentals data');
        }

        const result = await response.json();
        setRentals(result.records || []);
        setData(result.records?.slice(0, 10) || []);
      } catch (error) {
        console.error('Error fetching rentals data:', error);
      }
    }

    fetchRentals();
  }, [burl, userid]); // ✅ Runs only when userid is set

  // ✅ Filter and paginate data for the table
  useEffect(() => {
    let filteredData = Rentals;

    if (searchfilter) {
      filteredData = Rentals.filter((item) =>
        item.customer?.name?.toLowerCase().includes(searchfilter.toLowerCase()),
      );
    }

    const start = (current - 1) * 10;
    setData(filteredData.slice(start, start + 10));
  }, [searchfilter, Rentals, current]);

  const onSelectAll = useCallback(
    (checked: boolean) => {
      const updatedData = Rentals.map((item) => ({
        ...item,
        checked,
      }));
      setRentals(updatedData);
      setData(updatedData.slice((current - 1) * 10, current * 10));
    },
    [Rentals, current],
  );

  const onChange = useCallback(
    (row: any) => {
      const updatedData = Rentals.map((item) =>
        item.id === row.id ? { ...item, checked: !item.checked } : item,
      );
      setRentals(updatedData);
      setData(updatedData.slice((current - 1) * 10, current * 10));
    },
    [Rentals, current],
  );

  const onHeaderClick = useCallback(
    (value: string) => ({
      onClick: () => {
        setColumn(value);
        setOrder(order === 'desc' ? 'asc' : 'desc');
        const sortedData = [...Rentals].sort((a, b) =>
          order === 'desc'
            ? a[value] > b[value]
              ? -1
              : 1
            : a[value] > b[value]
              ? 1
              : -1,
        );
        setRentals(sortedData);
        setData(sortedData.slice((current - 1) * 10, current * 10));
      },
    }),
    [Rentals, order, current],
  );

  const columns = useMemo(
    () =>
      reservationColumn(
        order,
        column,
        onSelectAll,
        onChange,
        (e, row) => console.log(e.target.id, row),
        onHeaderClick,
      ),
    [order, column, onSelectAll, onChange, onHeaderClick],
  );

  return (
    <>
      <div className="mb-4 grid grid-cols-1 items-center gap-3 sm:grid-cols-[1fr_262px] md:gap-5 xl:gap-10">
        <Text tag="h4" className="text-xl">
          Transaction Activity
        </Text>
        <Input
          type="text"
          variant="outline"
          placeholder="Search by name"
          startIcon={<MagnifyingGlassIcon className="h-auto w-5" />}
          value={searchfilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          inputClassName="pl-12"
        />
      </div>
      <Table
        data={data}
        columns={columns}
        variant="minimal"
        className="text-sm"
      />
      <div className="mt-8 flex justify-end">
        <Pagination
          current={current}
          total={Rentals.length}
          pageSize={10}
          nextIcon="Next"
          prevIcon="Previous"
          variant="solid"
          prevIconClassName="!text-gray-dark"
          nextIconClassName="!text-gray-dark"
          onChange={(page) => setCurrent(page)}
        />
      </div>
    </>
  );
}
